import { getPerformanceVisual, hexToRgba } from "./reportTokens";

// Real flame icon path (apps/b2b-mobile/src/assets/icon/FireIcon2.svg), reused at three nested
// scales for the outer/mid/inner layers.
const FIRE_PATH_D =
  "M 381.63997,1200 C 135.77919,1061.434 71.049038,930.27865 108.05732,751.14866 135.37841,618.87726 224.83888,511.26304 233.417,379.24524 271.63184,448.78945 287.59935,498.9368 291.87036,571.60898 413.41348,422.69507 493.73121,216.54632 498.48692,0 c 0,0 316.57523,186.01008 337.34836,466.98023 27.25312,-57.91289 40.97132,-149.89172 13.7182,-209.5043 C 931.31098,317.09086 1409.8464,846.31428 784.73519,1200 902.263,971.16186 815.05535,662.38827 610.99652,519.78234 624.62426,581.10164 600.73114,809.80288 510.45434,910.29655 535.46754,742.38092 486.6538,671.37843 486.6538,671.37843 c 0,0 -16.75337,94.05444 -81.7575,189.06609 C 345.53708,947.20639 304.40709,1039.2914 381.64021,1200 z";
const FIRE_ICON_VIEWBOX = 1200;

const BASE_WIDTH = 130;
const BASE_HEIGHT = 100;
/** Horizontal center every layer is re-centered to, in BASE units. */
const FLAME_CENTER_X = 65;
/** Slack around the flame for the blurred silhouette to fade into, cancelled by a negative margin
 * so it costs no layout. */
const GLOW_PADDING = 60;
const RENDER_SCALE = 0.7;

// Coordinates in the icon's native 0-1200 space, not BASE units — shared by every layer.
const GRADIENT_START_Y = 1180;
const GRADIENT_END_Y = 90;
const GRADIENT_X = 600;

function makeLayerGeometry(scale: number, baseY: number) {
  const iconScale = (BASE_HEIGHT / FIRE_ICON_VIEWBOX) * scale;
  const size = FIRE_ICON_VIEWBOX * iconScale;
  return { iconScale, shiftX: FLAME_CENTER_X - size / 2, shiftY: baseY - size };
}

// baseY values tuned by hand on-device to avoid a dark cut-out between layers.
const LAYERS = [
  { key: "outer", geometry: makeLayerGeometry(1, 100) },
  { key: "mid", geometry: makeLayerGeometry(0.75, 104) },
  { key: "inner", geometry: makeLayerGeometry(0.55, 103) },
] as const;

const HIGHLIGHT = { cx: 60, cy: 56, r: 6 };
const BLOB = { cx: FLAME_CENTER_X, cy: 62, r: 36, blur: 22 };
const SILHOUETTE_BLUR = 13;

/**
 * The student app's flame, redrawn in SVG: one silhouette at three nested scales, all breathing on
 * the same cycle, over a blurred copy of itself. Static gradients — only heat and glow change.
 */
export function PerformanceFlame({ percent }: { percent: number }) {
  const visual = getPerformanceVisual(percent);
  const breatheDuration = `${Math.round(2200 / visual.energy)}ms`;

  return (
    <svg
      aria-hidden="true"
      width={(BASE_WIDTH + GLOW_PADDING * 2) * RENDER_SCALE}
      height={(BASE_HEIGHT + GLOW_PADDING * 2) * RENDER_SCALE}
      viewBox={`${-GLOW_PADDING} ${-GLOW_PADDING} ${BASE_WIDTH + GLOW_PADDING * 2} ${BASE_HEIGHT + GLOW_PADDING * 2}`}
      style={{ margin: -GLOW_PADDING * RENDER_SCALE }}
    >
      <defs>
        {LAYERS.map(({ key }) => (
          <linearGradient
            key={key}
            id={`flame-${key}-${visual.min}`}
            gradientUnits="userSpaceOnUse"
            x1={GRADIENT_X}
            y1={GRADIENT_START_Y}
            x2={GRADIENT_X}
            y2={GRADIENT_END_Y}
          >
            <stop offset="0" stopColor={visual[key][0]} />
            <stop offset="1" stopColor={visual[key][1]} />
          </linearGradient>
        ))}
        <linearGradient
          id={`flame-silhouette-${visual.min}`}
          gradientUnits="userSpaceOnUse"
          x1={GRADIENT_X}
          y1={GRADIENT_START_Y}
          x2={GRADIENT_X}
          y2={GRADIENT_END_Y}
        >
          <stop offset="0" stopColor={visual.glow} />
          <stop offset="1" stopColor={visual.outer[1]} />
        </linearGradient>
        <radialGradient id={`flame-blob-${visual.min}`}>
          <stop offset="0" stopColor={visual.glow} />
          <stop offset="1" stopColor={hexToRgba(visual.glow, 0)} />
        </radialGradient>
        <radialGradient id="flame-highlight">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="1" stopColor="rgba(255, 255, 255, 0)" />
        </radialGradient>
        <filter id="flame-silhouette-blur" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation={SILHOUETTE_BLUR} />
        </filter>
        <filter id="flame-blob-blur" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation={BLOB.blur} />
        </filter>
      </defs>

      <circle
        cx={BLOB.cx}
        cy={BLOB.cy}
        r={BLOB.r}
        fill={`url(#flame-blob-${visual.min})`}
        filter="url(#flame-blob-blur)"
        opacity={0.14 + visual.glowStrength * 0.22}
      />

      {/* Light coming off the flame, not a patch behind it: the outer silhouette, blurred. */}
      <g
        className="report-flame-layer"
        style={{ animationDuration: breatheDuration }}
        filter="url(#flame-silhouette-blur)"
        opacity={Math.min(0.7 + visual.glowStrength * 0.45, 1)}
      >
        <g
          transform={`translate(${LAYERS[0].geometry.shiftX} ${LAYERS[0].geometry.shiftY}) scale(${LAYERS[0].geometry.iconScale})`}
        >
          <path d={FIRE_PATH_D} fill={`url(#flame-silhouette-${visual.min})`} />
        </g>
      </g>

      {LAYERS.map(({ key, geometry }) => (
        <g key={key} className="report-flame-layer" style={{ animationDuration: breatheDuration }}>
          <g
            transform={`translate(${geometry.shiftX} ${geometry.shiftY}) scale(${geometry.iconScale})`}
          >
            <path d={FIRE_PATH_D} fill={`url(#flame-${key}-${visual.min})`} />
          </g>
        </g>
      ))}

      <circle
        cx={HIGHLIGHT.cx}
        cy={HIGHLIGHT.cy}
        r={HIGHLIGHT.r}
        fill="url(#flame-highlight)"
        opacity={0.16 + visual.energy * 0.08}
      />
    </svg>
  );
}
